import { isImageEmpty, isImageReady } from '@/ui/editor/image-readiness'
import { campaignsShowQueryKey, useCampaignsShow, useWelcomeScreensUpdate } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useCallback } from 'react'
import {
	activeLandingChatToolAtom,
	ConformedCampaignLandingPage,
	conformLandingPage,
	useLandingPageEditor,
} from '../../ui'

export function useNewLandingPageEditor(campaignId: string) {
	const campaign = useCampaignsShow(campaignId)
	const queryClient = useQueryClient()

	const welcomeScreen = campaign.data?.welcomeScreen

	const landingPage: ConformedCampaignLandingPage | undefined = welcomeScreen && welcomeScreen.id
		? conformLandingPage({
			id: welcomeScreen.id,
			title: welcomeScreen.title ?? '',
			subtitle: welcomeScreen.subtitle ?? '',
			body: welcomeScreen.description ?? '',
			imageId: welcomeScreen.image?.id ?? '',
			// `__ui` is the editor's own blob stored inside the screen's meta. The
			// spec types meta's values as unknown, so its shape is named once here.
			meta: (welcomeScreen.meta?.__ui as Record<string, unknown> | undefined) ?? {},
		})
		: undefined

	const imagesHistory = welcomeScreen?.imagesHistory?.filter((image) => !isImageEmpty(image)).map((image) => ({
		id: image.id,
		category: 'image' as const,
		collection: image.collectionName,
		name: image.name,
		fileName: image.fileName,
		humanReadableSize: image.humanReadableSize,
		createdAt: new Date(image.createdAt),
		url: image.url,
		width: image.width,
		height: image.height,
		isReady: isImageReady(image),
	}))

	const image = welcomeScreen?.image
		? {
			id: welcomeScreen.image.id,
			url: welcomeScreen.image.url,
			isReady: isImageReady(welcomeScreen.image),
			isEmpty: isImageEmpty(welcomeScreen.image),
		}
		: undefined

	const { mutateAsync: welcomeScreenMutationAsync } = useWelcomeScreensUpdate({
		mutationKey: ['update-welcome-screen'],
		onMutate: async () => {
			await queryClient.cancelQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	const handleLandingPageChange = useCallback(
		async (value: ConformedCampaignLandingPage) => {
			if (!welcomeScreen) {
				throw new Error('Welcome screen not found')
			}

			await welcomeScreenMutationAsync({
				welcomeScreen: welcomeScreen.id ?? '',
				data: {
					...welcomeScreen,
					title: value.title,
					subtitle: value.subtitle,
					description: value.body,
					imageId: value.imageId,
					meta: {
						...(welcomeScreen.meta ?? {}),
						__ui: value.meta,
					},
				},
			})
		},
		[welcomeScreen, welcomeScreenMutationAsync],
	)

	const isToolExecuting = useAtomValue(activeLandingChatToolAtom)

	useLandingPageEditor({
		landingPage,
		isPending: campaign.isPending,
		isGenerating: welcomeScreen?.isGenerated !== true,
		isToolExecuting,
		imagesHistory,
		image,
		onLandingPageChange: handleLandingPageChange,
		landingPageVersion: welcomeScreen?.updatedAt,
	})

	return {
		isGenerating: welcomeScreen?.isGenerated !== true,
	}
}
